import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {DataPointColumnsToDataType, DataType} from 'data-frames/DataFrameTypes';
import {ColumnValidationMap} from 'data-frames/util/AccessorUtils';
import assertWellFormedColumn from 'data-frames/util/legacy/assertWellFormedColumn';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import ImmutableSet from 'util/ImmutableSet';

import BenchlingChart from './internals/BenchlingChart';
import CartesianPlot from './internals/cartesian-plots/CartesianPlot';
import CartesianPoints from './internals/cartesian-plots/CartesianPoints';
import RegressionLine from './internals/cartesian-plots/RegressionLine';
import RegressionSummary from './internals/cartesian-plots/RegressionSummary';
import getCartesianAccessors from './internals/cartesian-plots/utils/getCartesianAccessors';
import {getCartesianErrorRangeExtents} from './internals/cartesian-plots/utils/getCartesianErrorRange';
import useSeriesExcludedState from './internals/hooks/useSeriesExcludedState';
import {LegendLocation} from './internals/LegendConstants';
import SeriesLegend, {useSeriesLegend} from './internals/SeriesLegend';
import getSeriesColorConfig, {getColorForDataset} from './internals/utils/getSeriesColorConfig';
import getVisxAxisConfig from './internals/utils/getVisxAxisConfig';
import {RegressionXAxisTSTypes} from './statistics/regression/RegressionTypes';
import {
  AXIS_COMPATIBLE_DATA_TYPES,
  AxisTypes,
  CartesianPoint as CartesianPointType,
  Dataset,
  datasetHasRegression,
  Datasets,
  ErrorBarConfig,
  isLabeledDataset,
  LabelCompatibleType,
  LegendConfig,
  ContinuousAxisConfig,
} from './Types';

// Define a special type here as scatter plots need to handle both numeric and string values, and map them
// to a continuous axis
export interface ScatterPlotXAxisConfig extends ContinuousAxisConfig {
  // if true, sorts domain in ascending order. Defaults to true if not specified
  sortDomain?: boolean;
}

export interface ScatterPlotConfig {
  colorPalette: ColorPalette;
  xAxis: ScatterPlotXAxisConfig;
  yAxis: ContinuousAxisConfig;
  errorBarConfig?: ErrorBarConfig;
  legend?: LegendConfig;
}

/**
 * @deprecated TODO(BNCH-24546): Remove legacy axis types in favor for the new SCATTER_PLOT_TYPE_VALIDATION_MAP
 */
export type ScatterPlotAxisTypes = AxisTypes;

/**
 * @deprecated TODO(BNCH-24546): Remove legacy axis types in favor for the new SCATTER_PLOT_TYPE_VALIDATION_MAP
 */
export const SCATTER_PLOT_AXIS_TYPES: {
  [key in keyof ScatterPlotAxisTypes]: Array<ScatterPlotAxisTypes[key]>;
} = {
  x: AXIS_COMPATIBLE_DATA_TYPES,
  y: [DataType.NUMBER],
  isMasked: [DataType.BOOLEAN],
  isOutlier: [DataType.BOOLEAN],
};

/**
 * @deprecated TODO(BNCH-24546): Remove legacy data in favor for the new DataFrame
 */
export interface ScatterPlotData<DataPoint extends CartesianPointType = CartesianPointType> {
  datasets: Datasets<DataPoint>;
  axisTypes: DataPointColumnsToDataType<DataPoint> & ScatterPlotAxisTypes;
}

export interface ScatterPlotProps<DataPoint extends CartesianPointType = CartesianPointType> {
  config: ScatterPlotConfig;
  data: ScatterPlotData<DataPoint>;
  onClickPoint?: (dataPoint: DataPoint) => void;
}

type ScatterPlotColumns = 'x' | 'y' | 'series' | 'isMasked' | 'isOutlier';

export const SCATTER_PLOT_X_AXIS_ALLOWED_LEAF_TYPES = [
  LeafType.DATE,
  LeafType.DATETIME,
  LeafType.DECIMAL,
  LeafType.INTEGER,
  LeafType.TEXT,
];

export const SCATTER_PLOT_TYPE_VALIDATION_MAP: ColumnValidationMap<ScatterPlotColumns> = {
  x: {
    allowedLeafTypes: SCATTER_PLOT_X_AXIS_ALLOWED_LEAF_TYPES,
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  y: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  series: {
    allowedLeafTypes: [LeafType.TEXT, LeafType.DECIMAL, LeafType.INTEGER],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  // TODO(BNCH-24546): Don't define validation options for isMasked once ScatterPlot is implemented to use
  // the new DataFrame, which has a masked column as a system column.
  isMasked: {
    allowedLeafTypes: [LeafType.BOOLEAN],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  isOutlier: {
    allowedLeafTypes: [LeafType.BOOLEAN],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
};

export const SCATTER_PLOT_REGRESSION_EXTRA_MARGIN = {top: 8, left: 0, right: 0, bottom: 0};

export function shouldRenderDataset(
  dataset: Dataset<CartesianPointType>,
  excludedSeries: ImmutableSet<LabelCompatibleType>
): boolean {
  return !isLabeledDataset(dataset) || !excludedSeries.has(dataset.label as LabelCompatibleType);
}

/**
 * Scatter plot component.
 *
 * Preconditions:
 *   - Expects valid axis configs
 *   - Expects all points in a single dataset to share the same label
 *   - If there are multiple datasets, expects each dataset to have a unique label
 *
 *
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export function ScatterPlot<DataPoint extends CartesianPointType = CartesianPointType>(
  props: ScatterPlotProps<DataPoint> & {height: number; width: number}
): JSX.Element {
  const {
    data: {axisTypes, datasets},
    config: {colorPalette, xAxis, yAxis},
  } = props;

  const {excludedSeries, toggleExcludedSeries} = useSeriesExcludedState();
  const handleOnClickLegendItem = (label: string): void => {
    toggleExcludedSeries(label);
  };

  const xyAccessors = getCartesianAccessors(axisTypes);

  const filteredDatasets = (datasets as Array<Dataset>)
    .filter((dataset) => shouldRenderDataset(dataset, excludedSeries))
    .map(
      (dataset) =>
        ({
          ...dataset,
          points: dataset.points.filter((point) => {
            const yValue = xyAccessors.yAccessor(point) as number;
            return (yAxis.min == null || yValue >= yAxis.min) && (yAxis.max == null || yValue <= yAxis.max);
          }),
        }) as Dataset
    );

  // Check that either there are no labeled regressions or every regression is labeled and matches
  // its corresponding dataset's label
  const datasetsWithLabeledRegressions = filteredDatasets.filter(
    (dataset) =>
      datasetHasRegression(dataset) &&
      dataset.regressionAnalysis.label != null &&
      isLabeledDataset(dataset) &&
      dataset.label === dataset.regressionAnalysis.label
  );
  const shouldPlotMultipleRegressions = datasetsWithLabeledRegressions.length > 0;

  const points = filteredDatasets.flatMap((dataset) => dataset.points);

  assertWellFormedColumn({rows: points, columnTypes: axisTypes}, 'isMasked');
  assertWellFormedColumn({rows: points, columnTypes: axisTypes}, 'isOutlier');
  assertWellFormedColumn({rows: points, columnTypes: axisTypes}, 'pointWarning');

  const xValues = points.map((point) => xyAccessors.xAccessor(point));
  // For the y-axis, we need the largest and smallest values, accounting for error ranges
  const yValues = getCartesianErrorRangeExtents(points);

  const visxXAxis = getVisxAxisConfig(xAxis, axisTypes.x, xValues, {sortDomain: xAxis.sortDomain ?? true});
  const visxYAxis = getVisxAxisConfig(yAxis, axisTypes.y, yValues, {
    extentOverride: [yAxis.min ?? null, yAxis.max ?? null],
  });

  const colorConfig = getSeriesColorConfig(datasets, colorPalette);

  const {legendLocation, legendProps, remainingChartHeight, remainingChartWidth, showLegend} =
    useSeriesLegend(
      colorConfig,
      props.height,
      props.width,
      datasets,
      excludedSeries,
      props.config.legend,
      handleOnClickLegendItem
    );

  const extraMargin = datasets.some((dataset) => datasetHasRegression(dataset))
    ? SCATTER_PLOT_REGRESSION_EXTRA_MARGIN
    : undefined;

  return (
    <>
      {showLegend && legendLocation === LegendLocation.TOP ? <SeriesLegend {...legendProps} /> : null}
      {excludedSeries.size() === datasets.length ? (
        <div className={css(styles.banner)}>
          <InlineBanner bannerType={InlineBannerType.INFO} text="Please select a series." />
        </div>
      ) : (
        <svg height={remainingChartHeight} width={props.width}>
          <CartesianPlot
            xAxis={visxXAxis}
            yAxis={visxYAxis}
            height={remainingChartHeight}
            width={remainingChartWidth}
            extraMargin={extraMargin}
          >
            {filteredDatasets.map((dataset, i) =>
              shouldRenderDataset(dataset, excludedSeries) ? (
                <React.Fragment key={isLabeledDataset(dataset) ? (dataset.label as LabelCompatibleType) : 0}>
                  {datasetHasRegression(dataset) && (
                    <>
                      <RegressionLine
                        axisTypes={axisTypes}
                        dataset={dataset}
                        domain={visxXAxis.scale.domain() as Array<RegressionXAxisTSTypes>}
                        scale={xAxis.scale}
                        dataKey={`fitted-curve-${dataset.regressionAnalysis.label}`}
                        color={
                          shouldPlotMultipleRegressions ? getColorForDataset(dataset, colorConfig) : undefined
                        }
                      />
                      {!shouldPlotMultipleRegressions && (
                        <RegressionSummary regressionAnalysis={dataset.regressionAnalysis} />
                      )}
                    </>
                  )}
                  <CartesianPoints
                    axisTypes={axisTypes}
                    colorConfig={colorConfig}
                    dataset={dataset}
                    errorValueLabel={props.config.errorBarConfig?.errorValueLabel}
                    onClickPoint={props.onClickPoint}
                    xAxisLabel={props.config.xAxis.label}
                    yAxisLabel={props.config.yAxis.label}
                  />
                </React.Fragment>
              ) : null
            )}
          </CartesianPlot>
          {showLegend && legendLocation === LegendLocation.RIGHT ? <SeriesLegend {...legendProps} /> : null}
        </svg>
      )}
    </>
  );
}

/**
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export default function ScatterPlotWrapper<DataPoint extends CartesianPointType = CartesianPointType>(
  props: ScatterPlotProps<DataPoint>
): JSX.Element {
  return (
    <BenchlingChart dataTestAttrs={{component: 'ScatterPlot', element: 'root'}}>
      {({height, width}) => <ScatterPlot {...props} height={height} width={width} />}
    </BenchlingChart>
  );
}

const styles = StyleSheet.create({
  banner: {
    marginLeft: '20px',
    marginRight: '20px',
    marginTop: '10px',
  },
});
